import { useState } from 'react';
import { safeErrorMessage } from '@/shared/utils/errors';

export type Page<T> = { items: T[]; total: number; offset: number };

// Shared load/paginate/error state for the admin list pages (Users, Grant History,
// Feedback, per-user Video Jobs) — each previously hand-rolled the same
// items/total/offset/loading/loadingMore/error state plus a `load(offset, append)`
// closure with an identical then/catch/finally shape. `fetchPage` is called fresh
// each time (it closes over whatever filter state the caller currently has), so
// callers don't need useCallback — `load`'s identity is expected to change every
// render, which only matters for the mount-only effect that already ignores it.
export function usePaginatedList<T>(fetchPage: (offset: number) => Promise<Page<T>>) {
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = (nextOffset: number, append: boolean) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    fetchPage(nextOffset)
      .then((page) => {
        setItems((prev) => (append ? [...prev, ...page.items] : page.items));
        setTotal(page.total);
        setOffset(page.offset);
      })
      .catch((err: unknown) => setError(safeErrorMessage(err, 'Unable to load this list.')))
      .finally(() => {
        if (append) setLoadingMore(false);
        else setLoading(false);
      });
  };

  const hasMore = offset + items.length < total;
  const loadMore = () => load(offset + items.length, true);

  return { items, setItems, total, offset, loading, loadingMore, error, setError, hasMore, load, loadMore };
}
